/**
 * Model MEP page (Wave 3): generate panel (procedural, seed-rolled) → 2D plan
 * with device markers + zone tint, device details popover, findings panel
 * with overrides, circuits/zones views, and the BoQ CSV download.
 *
 * Devices are matched to the visible storey via their `space_id` (server
 * space ids — the storeys themselves are not exposed standalone); devices
 * without a space (e.g. panels in unmodeled circulation) stay visible on
 * every storey rather than silently disappearing.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "@tanstack/react-router";
import { useShallow } from "zustand/react/shallow";
import type { MechanicalOutput, MepDevice, MepDiscipline, MepDuct } from "../api/types";
import { AirflowTable } from "../components/AirflowTable";
import { ElectricalResults } from "../components/ElectricalResults";
import { MechanicalResults } from "../components/MechanicalResults";
import { CircuitsPanel } from "../components/CircuitsPanel";
import { ExportsMenu } from "../components/ExportsMenu";
import { FindingsPanel } from "../components/FindingsPanel";
import { GeneratePanel } from "../components/GeneratePanel";
import { PlanView } from "../components/PlanView";
import { ZoneLegend } from "../components/ZoneLegend";
import { DUCT_COLORS, assignDuctStoreys, ductColor, ductLength, ductSizeLabel } from "../mep/ducts";
import { kindLabel, markerColor, markerLegend } from "../mep/markers";
import { zoneTintBySpaceId } from "../mep/zones";
import { IDLE } from "../state/generateFlow";
import { useMepStore, type EmergencyOutputSlice } from "../state/mep";
import { useIstariStore } from "../state/store";
import { capabilityLabel, qualityCheckLabel } from "../components/ReadinessCards";
import { effectiveOccupancy, joinFootprints, storeyTabs } from "../wizard/plan";

// Stable empty-settings identity: returning a fresh `{}` from a zustand
// selector makes useSyncExternalStore see a new snapshot every render
// (React #185 infinite-loop crash on the whole MEP page).
const EMPTY_HVAC_SETTINGS: Readonly<Record<string, never>> = Object.freeze({});
// Distinct sentinel for electrical (same frozen-empty value, own name) so the
// electrical fallback doesn't read as reusing HVAC's and the two can diverge
// later without a silent type mismatch.
const EMPTY_ELECTRICAL_SETTINGS: Readonly<Record<string, never>> = Object.freeze({});

// Stable empty-array identity for the per-model resource selectors: a single
// frozen [] keeps the reference stable across renders (so useShallow doesn't
// churn and downstream useMemo deps stay stable) while a model's rows are
// still undefined — replaces the old `useMemo(() => x ?? [], [x])` wrappers.
const EMPTY_ARR: never[] = Object.freeze([]) as never[];

// Stable empty emergency-slice identity (same rationale as EMPTY_ARR): a frozen
// value keeps the selector snapshot stable while a model's Output is undefined.
const EMPTY_EMERGENCY_SLICE: EmergencyOutputSlice = Object.freeze({
  system: null,
  branch: Object.freeze({}) as Record<string, never>,
  ats: EMPTY_ARR,
});

// Stable empty mechanical-slice identity (same rationale as EMPTY_ARR).
const EMPTY_MECHANICAL_SLICE: MechanicalOutput = Object.freeze({
  equipment: EMPTY_ARR,
  load_screening: EMPTY_ARR,
  boq: null,
});

const sectionTitle: CSSProperties = {
  margin: "1rem 0 0.5rem",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#8e8eb8",
};

function DuctDetails({ duct }: { duct: MepDuct }) {
  const rows: [string, string][] = [
    ["System", duct.system],
    ["Shape", duct.shape],
    ["Size", ductSizeLabel(duct)],
    ["Length", `${ductLength(duct.centerline).toFixed(1)} m`],
    ["Airflow", duct.cfm !== null ? `${Math.round(duct.cfm).toLocaleString()} cfm` : "—"],
    ["Velocity", duct.velocity_fpm !== null ? `${Math.round(duct.velocity_fpm)} fpm` : "—"],
  ];
  return (
    <div data-testid="duct-details">
      <h3 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem", display: "flex", gap: "0.4rem" }}>
        <span
          aria-hidden
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: ductColor(duct.system),
            display: "inline-block",
            marginTop: 3,
          }}
        />
        Duct {duct.id}
      </h3>
      <table style={{ width: "100%", borderSpacing: 0, fontSize: "0.82rem" }}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td style={{ color: "#8e8eb8", padding: "2px 6px 2px 0", verticalAlign: "top" }}>
                {label}
              </td>
              <td style={{ padding: "2px 0" }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeviceDetails({ device }: { device: MepDevice }) {
  const rows: [string, string][] = [
    ["Kind", kindLabel(device.kind)],
    ["Catalog item", device.catalog_item_id ?? "—"],
    ["Mount", device.mount ?? "—"],
    ["Position", `${device.x_m.toFixed(2)}, ${device.y_m.toFixed(2)}, ${device.z_m.toFixed(2)} m`],
    ["Candela", device.candela !== null ? `${device.candela} cd` : "—"],
    ["dBA @ 10 ft", device.dba_at_10ft !== null ? `${device.dba_at_10ft} dBA` : "—"],
    ["Circuit", device.circuit_id ?? "—"],
    ["Zone", device.zone_id ?? "—"],
    ["Origin", device.origin ?? "—"],
  ];
  return (
    <div data-testid="device-details">
      <h3 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem", display: "flex", gap: "0.4rem" }}>
        <span
          aria-hidden
          style={{
            width: 12,
            height: 12,
            borderRadius: 3,
            background: markerColor(device.kind),
            display: "inline-block",
            marginTop: 3,
          }}
        />
        Device {device.id}
      </h3>
      <table style={{ width: "100%", borderSpacing: 0, fontSize: "0.82rem" }}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td style={{ color: "#8e8eb8", padding: "2px 6px 2px 0", verticalAlign: "top" }}>
                {label}
              </td>
              <td style={{ padding: "2px 0" }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MepPage() {
  const params = useParams({ strict: false }) as { modelId?: string };
  const modelId = params.modelId ?? "";

  // Spaces + readiness from the Wave 2 store (footprints, gates).
  const spaces = useIstariStore((s) => s.spaces[modelId]);
  const spacesStatus = useIstariStore((s) => s.spacesStatus[modelId] ?? "idle");
  const loadSpaces = useIstariStore((s) => s.loadSpaces);
  const readiness = useIstariStore((s) => s.readiness[modelId]);
  const readinessStatus = useIstariStore((s) => s.readinessStatus[modelId] ?? "idle");
  const loadReadiness = useIstariStore((s) => s.loadReadiness);
  const summary = useIstariStore(
    (s) => s.models.find((m) => m.modelId === modelId)?.summary ?? null,
  );
  const projectId = useIstariStore(
    (s) => s.models.find((m) => m.modelId === modelId)?.projectId ?? null,
  );

  // Grouped, shallow-compared reads of the per-model MEP slice. Each group
  // returns an object compared field-by-field (useShallow), so this component
  // re-renders on the same value changes as the old 21 individual selectors —
  // but with one subscription per group. Array fields fall back to the shared
  // frozen EMPTY_ARR for a stable identity (see EMPTY_ARR), which is why the
  // downstream `useMemo(() => x ?? [])` wrappers are gone.
  const { flow, system } = useMepStore(
    useShallow((s) => ({
      flow: s.flow[modelId] ?? IDLE,
      system: s.activeSystem[modelId] ?? null,
    })),
  );
  const {
    devices,
    zones,
    circuits,
    findings,
    ducts,
    terminals,
    airflows,
    electricalLoadCalc,
    electricalPanelSchedule,
    electricalGrounding,
    electricalEmergency,
    electricalVoltageDrop,
    mechanicalOutput,
  } = useMepStore(
    useShallow((s) => ({
      devices: s.devices[modelId] ?? EMPTY_ARR,
      zones: s.zones[modelId] ?? EMPTY_ARR,
      circuits: s.circuits[modelId] ?? EMPTY_ARR,
      findings: s.findings[modelId] ?? EMPTY_ARR,
      ducts: s.ducts[modelId] ?? EMPTY_ARR,
      terminals: s.terminals[modelId] ?? EMPTY_ARR,
      airflows: s.airflows[modelId] ?? EMPTY_ARR,
      electricalLoadCalc: s.electricalLoadCalc[modelId],
      electricalPanelSchedule: s.electricalPanelSchedule[modelId],
      electricalGrounding: s.electricalGrounding[modelId] ?? null,
      electricalEmergency: s.electricalEmergency[modelId] ?? EMPTY_EMERGENCY_SLICE,
      electricalVoltageDrop: s.electricalVoltageDrop[modelId] ?? null,
      mechanicalOutput: s.mechanicalOutput[modelId] ?? EMPTY_MECHANICAL_SLICE,
    })),
  );
  const { hvacSettings, electricalSettings, pinnedSeed, overrideError } = useMepStore(
    useShallow((s) => ({
      hvacSettings: s.hvacSettings[modelId] ?? EMPTY_HVAC_SETTINGS,
      electricalSettings: s.electricalSettings[modelId] ?? EMPTY_ELECTRICAL_SETTINGS,
      pinnedSeed: s.pinnedSeed[modelId] ?? null,
      overrideError: s.overrideError,
    })),
  );
  const {
    generate,
    setPinnedSeed,
    setHvacSettings,
    setElectricalSettings,
    override,
    clearOverride,
  } = useMepStore(
    useShallow((s) => ({
      generate: s.generate,
      setPinnedSeed: s.setPinnedSeed,
      setHvacSettings: s.setHvacSettings,
      setElectricalSettings: s.setElectricalSettings,
      override: s.override,
      clearOverride: s.clearOverride,
    })),
  );

  const autoAssign = useIstariStore((s) => s.autoAssign);

  const [discipline, setDiscipline] = useState<MepDiscipline>("fire_alarm");
  const [autoFillBusy, setAutoFillBusy] = useState(false);
  const [activeStorey, setActiveStorey] = useState<number | null | undefined>(undefined);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedDuctId, setSelectedDuctId] = useState<string | null>(null);
  const [hiddenKinds, setHiddenKinds] = useState<ReadonlySet<string>>(new Set());
  const [ductsVisible, setDuctsVisible] = useState(true);

  // Stable selection handlers so the memoized plan markers don't re-render on
  // every parent render (state setters are already stable, so deps are empty).
  const selectSpace = useCallback((id: string) => {
    setSelectedSpaceId(id);
    setSelectedDeviceId(null);
    setSelectedDuctId(null);
  }, []);
  const selectDevice = useCallback((id: string) => {
    setSelectedDeviceId(id);
    setSelectedSpaceId(null);
    setSelectedDuctId(null);
  }, []);
  const selectDuct = useCallback((id: string) => {
    setSelectedDuctId(id);
    setSelectedSpaceId(null);
    setSelectedDeviceId(null);
  }, []);

  useEffect(() => {
    if (modelId && spacesStatus === "idle") void loadSpaces(modelId);
  }, [modelId, spacesStatus, loadSpaces]);
  useEffect(() => {
    if (modelId && readinessStatus === "idle") void loadReadiness(modelId);
  }, [modelId, readinessStatus, loadReadiness]);

  // `spaces` comes from the istari store; the MEP arrays are already defaulted
  // to a stable EMPTY_ARR by their grouped selector, so these are plain
  // stable-identity aliases (no useMemo needed).
  const rows = spaces ?? EMPTY_ARR;
  const allDevices = devices;
  const allZones = zones;
  const allDucts = ducts;
  const allTerminals = terminals;
  const allAirflows = airflows;

  const tabs = useMemo(() => storeyTabs(rows), [rows]);
  const storey = activeStorey !== undefined ? activeStorey : tabs[0]?.storeyId;
  const storeySpaces = useMemo(
    () => rows.filter((s) => s.storey_ifc_id === (storey ?? null)),
    [rows, storey],
  );
  const planSpaces = useMemo(() => joinFootprints(storeySpaces, summary), [storeySpaces, summary]);

  const storeyDevices = useMemo(() => {
    const spaceIds = new Set(storeySpaces.map((s) => s.id));
    return allDevices.filter(
      (d) => !hiddenKinds.has(d.kind) && (d.space_id === null || spaceIds.has(d.space_id)),
    );
  }, [allDevices, storeySpaces, hiddenKinds]);

  // HVAC: terminals/airflows match the storey via space id (like devices);
  // ducts carry no storey id, so they map through the z-derived assignment to
  // the server storey ids the visible storey's terminals carry.
  const storeyTerminals = useMemo(() => {
    if (!ductsVisible) return [];
    const spaceIds = new Set(storeySpaces.map((s) => s.id));
    return allTerminals.filter((t) => t.space_id === null || spaceIds.has(t.space_id));
  }, [allTerminals, storeySpaces, ductsVisible]);

  // Mechanical equipment is keyed to its host space, so filter to the visible
  // storey by space membership — the same basis storeyTerminals uses.
  const storeyMechanical = useMemo(() => {
    const spaceIds = new Set(storeySpaces.map((s) => s.id));
    return (mechanicalOutput.equipment ?? []).filter((e) => spaceIds.has(e.space_id));
  }, [mechanicalOutput, storeySpaces]);

  const ductStoreys = useMemo(
    () => assignDuctStoreys(allDucts, allTerminals),
    [allDucts, allTerminals],
  );
  const storeyDucts = useMemo(() => {
    if (!ductsVisible) return [];
    const spaceIds = new Set(storeySpaces.map((s) => s.id));
    const serverStoreyIds = new Set(
      allTerminals
        .filter((t) => t.space_id !== null && spaceIds.has(t.space_id) && t.storey_id !== null)
        .map((t) => t.storey_id!),
    );
    return allDucts.filter((d) => {
      const assigned = ductStoreys.get(d.id) ?? null;
      // No inference basis → keep visible rather than vanish.
      return assigned === null || serverStoreyIds.has(assigned);
    });
  }, [allDucts, allTerminals, ductStoreys, storeySpaces, ductsVisible]);

  const storeyAirflows = useMemo(() => {
    const spaceIds = new Set(storeySpaces.map((s) => s.id));
    return allAirflows.filter((a) => spaceIds.has(a.space_id));
  }, [allAirflows, storeySpaces]);

  const healthcareSuggested = useMemo(
    () =>
      rows.some((s) => {
        const occ = s.effective_occupancy ?? effectiveOccupancy(s);
        return occ === "patient_room" || occ === "operating_room";
      }),
    [rows],
  );

  const zoneTints = useMemo(() => zoneTintBySpaceId(allZones), [allZones]);
  const legend = useMemo(() => markerLegend(allDevices.map((d) => d.kind)), [allDevices]);
  const selectedDevice = allDevices.find((d) => d.id === selectedDeviceId) ?? null;
  const selectedSpace = rows.find((s) => s.id === selectedSpaceId) ?? null;
  const selectedDuct = allDucts.find((d) => d.id === selectedDuctId) ?? null;
  const isHvac = system?.discipline === "hvac";
  const isElectrical = system?.discipline === "electrical";

  // Generation gate from the readiness report (never confident-wrong output).
  const gate = readiness?.generation.find((g) => g.kind === discipline) ?? null;
  const enabled = gate?.enabled ?? false;
  const blockedReason =
    gate && !gate.enabled
      ? [
          ...gate.blocked_by.map(capabilityLabel),
          // Quality-only blocks (every capability Found, but the model's
          // quality evaluation isn't generation-eligible) leave `blocked_by`
          // empty — without this, the banner fell back to the generic
          // "model data missing", hiding the real reason (PR #100 review).
          ...(gate.blocked_by_quality ?? []).map(qualityCheckLabel),
        ].join(", ") || "model data missing"
      : readiness
        ? null
        : "readiness report not loaded";

  const seed =
    flow.phase === "running" || flow.phase === "done" ? flow.seed : (system?.seed ?? null);

  const toggleKind = (kind: string) => {
    setHiddenKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  };

  return (
    <main
      data-testid="mep-page"
      style={{
        fontFamily: "sans-serif",
        padding: "1rem 1.5rem",
        background: "#1a1a2e",
        color: "#d8d8e8",
        minHeight: "100vh",
        boxSizing: "border-box",
        maxWidth: 1100,
        margin: "0 auto",
      }}
    >
      <nav style={{ marginBottom: "0.6rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {projectId !== null && (
          <a
            data-testid="mep-project-link"
            href={`/projects/${projectId}`}
            style={{ color: "#8e8eb8" }}
          >
            ← Project
          </a>
        )}
        <a href={`/models/${modelId}/readiness`} style={{ color: "#8e8eb8" }}>
          Readiness report
        </a>
        <a href={`/models/${modelId}/normalize`} style={{ color: "#8e8eb8" }}>
          Normalization wizard
        </a>
        <a
          data-testid="mep-viewer-link"
          href={`/models/${modelId}/viewer`}
          style={{ color: "#8e8eb8" }}
        >
          3D viewer
        </a>
      </nav>
      <h1 style={{ margin: "0 0 0.6rem", fontSize: "1.2rem" }}>MEP generation</h1>

      {readiness && gate && !enabled && (
        <div
          data-testid="mep-autofill-banner"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.8rem",
            flexWrap: "wrap",
            background: "#21213a",
            borderRadius: 8,
            padding: "0.6rem 0.9rem",
            marginBottom: "0.6rem",
            fontSize: "0.85rem",
          }}
        >
          <span style={{ color: "#e5c07b" }}>Generation blocked: {blockedReason}.</span>
          <button
            type="button"
            data-testid="mep-autofill-button"
            onClick={() => {
              setAutoFillBusy(true);
              void autoAssign(modelId).finally(() => setAutoFillBusy(false));
            }}
            disabled={autoFillBusy}
            style={{
              padding: "0.35rem 0.8rem",
              borderRadius: 6,
              border: "1px solid #4a90d9",
              background: "#4a90d9",
              color: "#fff",
              cursor: autoFillBusy ? "wait" : "pointer",
            }}
          >
            {autoFillBusy ? "Assigning…" : "Auto-assign occupancy & exits"}
          </button>
        </div>
      )}

      <GeneratePanel
        discipline={discipline}
        onDisciplineChange={setDiscipline}
        flow={flow}
        seed={seed}
        pinned={pinnedSeed !== null}
        onTogglePin={() => setPinnedSeed(modelId, pinnedSeed !== null ? null : seed)}
        onReroll={() => {
          setPinnedSeed(modelId, null);
          void generate(modelId, discipline);
        }}
        onGenerate={() => void generate(modelId, discipline)}
        enabled={enabled}
        blockedReason={blockedReason}
        hvacSettings={hvacSettings}
        onHvacSettingsChange={(patch) => setHvacSettings(modelId, patch)}
        electricalSettings={electricalSettings}
        onElectricalSettingsChange={(patch) => setElectricalSettings(modelId, patch)}
        healthcareSuggested={healthcareSuggested}
      />

      {system && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            margin: "0.6rem 0 0",
            fontSize: "0.85rem",
            color: "#8e8eb8",
          }}
        >
          <span data-testid="system-status">
            {system.discipline} · {system.status}
            {system.ruleset_hash && ` · ruleset ${system.ruleset_hash.slice(0, 8)}`}
          </span>
          {system.status === "complete" && (
            <a
              data-testid="view-in-3d-link"
              href={`/models/${modelId}/viewer`}
              style={{ color: "#4a90d9", fontWeight: 600 }}
            >
              View in 3D →
            </a>
          )}
        </div>
      )}

      <ExportsMenu system={system} modelId={modelId} />

      {tabs.length > 0 && (
        <div style={{ display: "flex", gap: "0.4rem", margin: "0.6rem 0", flexWrap: "wrap" }}>
          {tabs.map((tab) => {
            const active = (storey ?? null) === tab.storeyId;
            return (
              <button
                key={tab.storeyId ?? "none"}
                type="button"
                data-testid="storey-tab"
                onClick={() => {
                  setActiveStorey(tab.storeyId);
                  setSelectedSpaceId(null);
                  setSelectedDeviceId(null);
                  setSelectedDuctId(null);
                }}
                style={{
                  padding: "0.35rem 0.8rem",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  background: active ? "#4a90d9" : "#2a2a44",
                  color: active ? "#fff" : "#d8d8e8",
                }}
              >
                {tab.name} ({tab.spaceCount})
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: "1rem" }}>
        <div style={{ flex: 1, minWidth: 0, height: 480 }}>
          <PlanView
            spaces={planSpaces}
            selectedSpaceId={selectedSpaceId}
            onSelect={selectSpace}
            devices={storeyDevices}
            selectedDeviceId={selectedDeviceId}
            onSelectDevice={selectDevice}
            zoneTints={zoneTints}
            ducts={storeyDucts}
            selectedDuctId={selectedDuctId}
            onSelectDuct={selectDuct}
            terminals={storeyTerminals}
            mechanicalEquipment={storeyMechanical}
          />
          {allDucts.length > 0 && (
            <div
              data-testid="duct-legend"
              style={{
                display: "flex",
                gap: "0.8rem",
                flexWrap: "wrap",
                marginTop: "0.5rem",
                fontSize: "0.78rem",
                color: "#8e8eb8",
              }}
            >
              <label
                data-testid="duct-layer-toggle"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
              >
                <input
                  type="checkbox"
                  checked={ductsVisible}
                  onChange={() => setDuctsVisible((v) => !v)}
                />
                Ductwork
              </label>
              {(["supply", "return"] as const).map((sys) => (
                <span
                  key={sys}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 4,
                      borderRadius: 2,
                      background: DUCT_COLORS[sys],
                      display: "inline-block",
                    }}
                  />
                  {sys}
                </span>
              ))}
            </div>
          )}
          {legend.length > 0 && (
            <div
              data-testid="device-legend"
              style={{
                display: "flex",
                gap: "0.8rem",
                flexWrap: "wrap",
                marginTop: "0.5rem",
                fontSize: "0.78rem",
                color: "#8e8eb8",
              }}
            >
              {legend.map(({ kind, color }) => (
                <label
                  key={kind}
                  data-testid="device-legend-row"
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
                >
                  <input
                    type="checkbox"
                    checked={!hiddenKinds.has(kind)}
                    onChange={() => toggleKind(kind)}
                  />
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: color,
                      display: "inline-block",
                    }}
                  />
                  {kindLabel(kind)}
                </label>
              ))}
            </div>
          )}
        </div>
        <aside
          style={{
            width: 300,
            flexShrink: 0,
            background: "#21213a",
            borderRadius: 8,
            padding: "0.9rem",
            overflowY: "auto",
            maxHeight: 480,
          }}
        >
          {selectedDuct ? (
            <DuctDetails duct={selectedDuct} />
          ) : selectedDevice ? (
            <DeviceDetails device={selectedDevice} />
          ) : selectedSpace ? (
            <div data-testid="space-details">
              <h3 style={{ margin: "0 0 0.4rem", fontSize: "0.95rem" }}>{selectedSpace.name}</h3>
              <p style={{ margin: 0, color: "#8e8eb8", fontSize: "0.82rem" }}>
                {selectedSpace.effective_occupancy ??
                  selectedSpace.occupancy_override ??
                  selectedSpace.occupancy ??
                  "unclassified"}
                {selectedSpace.area_m2 !== null && ` · ${selectedSpace.area_m2.toFixed(1)} m²`}
              </p>
            </div>
          ) : (
            <p data-testid="nothing-selected" style={{ color: "#8e8eb8" }}>
              Click a device, duct, or space on the plan for details.
            </p>
          )}
        </aside>
      </div>

      {isElectrical ? (
        <ElectricalResults
          loadCalc={electricalLoadCalc}
          panelSchedule={electricalPanelSchedule}
          groundingSchedule={electricalGrounding}
          emergencySystem={electricalEmergency.system}
          emergencyBranch={electricalEmergency.branch}
          ats={electricalEmergency.ats}
          voltageDropStudy={electricalVoltageDrop}
          systemId={system?.id ?? null}
        />
      ) : isHvac ? (
        // Mechanical is HVAC expanded: ductwork's airflow table AND the
        // equipment schedule are two sections of the ONE hvac result view.
        <>
          <h2 style={sectionTitle}>Airflow requirements</h2>
          <AirflowTable airflows={storeyAirflows} spaces={rows} />
          <MechanicalResults
            equipment={mechanicalOutput.equipment}
            loadScreening={mechanicalOutput.load_screening}
            boq={mechanicalOutput.boq}
            spaces={rows}
            systemId={system?.id ?? null}
          />
        </>
      ) : (
        <>
          <h2 style={sectionTitle}>Zones</h2>
          <ZoneLegend zones={allZones} />

          <h2 style={sectionTitle}>Circuits</h2>
          <CircuitsPanel circuits={circuits} />
        </>
      )}

      <h2 style={sectionTitle}>Findings</h2>
      <FindingsPanel
        findings={findings}
        onOverride={(findingId, reason) => void override(modelId, findingId, reason)}
        onClearOverride={(findingId) => void clearOverride(modelId, findingId)}
        overrideError={overrideError}
      />
    </main>
  );
}
